import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../Security/AuthProvider';
import { useNotification } from '../../../Utils/Commons/Notification/NotificationContext';
import './ClientRequest.css';
// OptimizedAPI supprimé

const ClientRequest = () => {
  const { credentials } = useAuth();
  const { showNotification } = useNotification();
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_city: '',
    client_postal_code: '',
    operation_name: '',
    operation_description: '',
    preferred_date: '',
    preferred_time: '',
    urgency_level: 'normal',
    additional_notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const required = ['client_name', 'client_email', 'client_phone', 'operation_name'];
    const missing = required.filter(field => !formData[field].trim());
    
    if (missing.length > 0) {
      showNotification('error', `Champs requis manquants : ${missing.join(', ')}`);
      return false;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.client_email)) {
      showNotification('error', 'Format d\'email invalide');
      return false;
    }

    // Validation téléphone basique
    const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
    if (!phoneRegex.test(formData.client_phone.replace(/\s/g, ''))) {
      showNotification('error', 'Format de téléphone invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Configuration OptimizedAPI avec credentials par défaut
      const requestBody = {
        ...formData,
        company_name: credentials?.company_name,
        company_password: credentials?.company_password,
        request_date: new Date().toISOString(),
        status: 'pending'
      };

      const data = await fetch('/rdv/make-appointment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }).then(res => res.json());

      if (data.success) {
        setSuccess(true);
        showNotification('success', 'Demande de rendez-vous créée avec succès !');
        
        // Reset form
        setFormData({
          client_name: '',
          client_email: '',
          client_phone: '',
          client_address: '',
          client_city: '',
          client_postal_code: '',
          operation_name: '',
          operation_description: '',
          preferred_date: '',
          preferred_time: '',
          urgency_level: 'normal',
          additional_notes: ''
        });
      } else {
        setError(data.error || 'Erreur lors de la création de la demande');
        showNotification('error', data.error || 'Erreur lors de la création de la demande');
      }
    } catch (error) {
      console.error('❌ [ClientRequest] Erreur:', error);
      setError('Erreur de connexion au serveur');
      showNotification('error', 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        ...formData,
        company_name: credentials?.company_name,
        company_password: credentials?.company_password,
        created_at: new Date().toISOString(),
        status: 'active'
      };

      const result = await fetch('/rdv/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }).then(res => res.json());

      if (result.success) {
        showNotification('success', 'Client créé avec succès !');
        // Invalider les caches
      } else {
        setError(result.error || 'Erreur lors de la création du client');
        showNotification('error', result.error || 'Erreur lors de la création du client');
      }
    } catch (error) {
      console.error('❌ [ClientRequest] Erreur création client:', error);
      setError('Erreur de connexion au serveur');
      showNotification('error', 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="client-request-success">
        <div className="success-content">
          <h2>✅ Demande créée avec succès !</h2>
          <p>Votre demande de rendez-vous a été enregistrée et sera traitée dans les plus brefs délais.</p>
          <button 
            onClick={() => setSuccess(false)}
            className="back-button"
          >
            Créer une nouvelle demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="client-request-container">
      <div className="client-request-header">
        <h1>Demande de Rendez-vous</h1>
        <p>Créez une nouvelle demande de rendez-vous pour un client</p>
      </div>

      <form onSubmit={handleSubmit} className="client-request-form">
        <div className="form-section">
          <h3>Informations Client</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_name">Nom complet *</label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                required
                placeholder="Nom et prénom du client"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="client_email">Email *</label>
              <input
                type="email"
                id="client_email"
                name="client_email"
                value={formData.client_email}
                onChange={handleInputChange}
                required
                placeholder="email@exemple.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_phone">Téléphone *</label>
              <input
                type="tel"
                id="client_phone"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleInputChange}
                required
                placeholder="06 12 34 56 78"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="urgency_level">Niveau d'urgence</label>
              <select
                id="urgency_level"
                name="urgency_level"
                value={formData.urgency_level}
                onChange={handleInputChange}
              >
                <option value="low">Faible</option>
                <option value="normal">Normal</option>
                <option value="high">Élevé</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_address">Adresse</label>
              <input
                type="text"
                id="client_address"
                name="client_address"
                value={formData.client_address}
                onChange={handleInputChange}
                placeholder="123 Rue de la Paix"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_city">Ville</label>
              <input
                type="text"
                id="client_city"
                name="client_city"
                value={formData.client_city}
                onChange={handleInputChange}
                placeholder="Paris"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="client_postal_code">Code postal</label>
              <input
                type="text"
                id="client_postal_code"
                name="client_postal_code"
                value={formData.client_postal_code}
                onChange={handleInputChange}
                placeholder="75001"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Détails de l'Opération</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="operation_name">Nom de l'opération *</label>
              <input
                type="text"
                id="operation_name"
                name="operation_name"
                value={formData.operation_name}
                onChange={handleInputChange}
                required
                placeholder="Installation thermostat connecté"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="operation_description">Description</label>
            <textarea
              id="operation_description"
              name="operation_description"
              value={formData.operation_description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Détails sur l'opération à effectuer..."
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Préférences de Rendez-vous</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preferred_date">Date préférée</label>
              <input
                type="date"
                id="preferred_date"
                name="preferred_date"
                value={formData.preferred_date}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="preferred_time">Heure préférée</label>
              <select
                id="preferred_time"
                name="preferred_time"
                value={formData.preferred_time}
                onChange={handleInputChange}
              >
                <option value="">Aucune préférence</option>
                <option value="morning">Matin (8h-12h)</option>
                <option value="afternoon">Après-midi (13h-17h)</option>
                <option value="evening">Soirée (17h-20h)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="additional_notes">Notes supplémentaires</label>
            <textarea
              id="additional_notes"
              name="additional_notes"
              value={formData.additional_notes}
              onChange={handleInputChange}
              rows="3"
              placeholder="Informations complémentaires..."
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Création...' : 'Créer la demande'}
          </button>
          
          <button
            type="button"
            onClick={handleCreateClient}
            disabled={loading}
            className="create-client-button"
          >
            {loading ? 'Création...' : 'Créer le client'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientRequest; 